import { useState, useMemo, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Button,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material'
import {
  DirectionsWalk,
  DirectionsBike,
  DirectionsCar,
  DirectionsBus,
  DirectionsBoat,
  Hiking,
  ElectricBike,
  Kayaking,
  Accessible,
  Refresh,
  Clear,
  UploadFile,
  Edit,
  Route as RouteIcon,
} from '@mui/icons-material'
import { useWorkflowStore, WorkflowNode } from '@store/workflowStore'
import { workflowDesignerApi } from '@api/endpoints/workflowDesigner'
import {
  RouteTravelMode,
  RouteLineStyle,
  RouteArrowStyle,
  RouteNodeConfig,
  RouteDataSource,
  RouteExportDisclosure,
  ROUTE_TRAVEL_MODES,
  ROUTE_LINE_STYLES,
  ROUTE_ARROW_STYLES,
  ROUTE_EXPORT_DISCLOSURE_OPTIONS,
  WorkflowNodeType,
} from '@features/adventures/types/workflow.types'
import { parseGpxFile, isGpxParseError } from '@features/adventures/utils/gpxParser'
import { RoutePointEditor } from './RoutePointEditor'

interface RouteNodePropertiesPanelProps {
  node: WorkflowNode
  adventureId: string
}

// Icon mapping for travel modes
const travelModeIcons: Record<RouteTravelMode, React.ReactNode> = {
  'foot-walking': <DirectionsWalk fontSize="small" />,
  'foot-hiking': <Hiking fontSize="small" />,
  'cycling': <DirectionsBike fontSize="small" />,
  'cycling-electric': <ElectricBike fontSize="small" />,
  'driving': <DirectionsCar fontSize="small" />,
  'public-transit': <DirectionsBus fontSize="small" />,
  'ferry': <DirectionsBoat fontSize="small" />,
  'water-kayak': <Kayaking fontSize="small" />,
  'wheelchair': <Accessible fontSize="small" />,
}

// Predefined colors for routes
const ROUTE_COLORS = [
  { value: '#9C27B0', label: 'Purple' },
  { value: '#2196F3', label: 'Blue' },
  { value: '#4CAF50', label: 'Green' },
  { value: '#FF9800', label: 'Orange' },
  { value: '#F44336', label: 'Red' },
  { value: '#00BCD4', label: 'Cyan' },
  { value: '#E91E63', label: 'Pink' },
  { value: '#795548', label: 'Brown' },
]

export function RouteNodePropertiesPanel({
  node,
  adventureId,
}: RouteNodePropertiesPanelProps) {
  const nodes = useWorkflowStore(state => state.nodes)
  const updateNode = useWorkflowStore(state => state.updateNode)

  const config = node.data.routeNodeConfig
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Local state
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPointEditor, setShowPointEditor] = useState(false)
  const [showAutoRouteDialog, setShowAutoRouteDialog] = useState(false)

  // Local state for auto-route dialog selections
  const [dialogSourceId, setDialogSourceId] = useState(config?.sourceNodeId || '')
  const [dialogDestId, setDialogDestId] = useState(config?.destinationNodeId || '')
  const [dialogTravelMode, setDialogTravelMode] = useState<RouteTravelMode>(config?.travelMode || 'foot-walking')

  // Get available POI Location nodes for selection
  const locationNodes = useMemo(() =>
    nodes.filter(n => n.data.nodeType === WorkflowNodeType.PoiLocation),
    [nodes]
  )

  // Create options for autocomplete
  const locationOptions = useMemo(() =>
    locationNodes.map(n => ({
      id: n.id,
      label: `L${n.data.sequenceNumber || '?'} - ${n.data.label}`,
      node: n,
    })),
    [locationNodes]
  )

  // Find selected nodes
  const sourceNode = useMemo(() =>
    nodes.find(n => n.id === config?.sourceNodeId),
    [nodes, config?.sourceNodeId]
  )
  const destinationNode = useMemo(() =>
    nodes.find(n => n.id === config?.destinationNodeId),
    [nodes, config?.destinationNodeId]
  )

  // Get location configs
  const sourceLocation = sourceNode?.data.poiLocationConfig
  const destLocation = destinationNode?.data.poiLocationConfig

  // Check if we can generate a route (using current config)
  const hasValidLocations = !!(
    sourceLocation?.latitude && sourceLocation?.longitude &&
    destLocation?.latitude && destLocation?.longitude
  )

  // Check if dialog selections have valid locations
  const dialogSourceNode = useMemo(() =>
    nodes.find(n => n.id === dialogSourceId),
    [nodes, dialogSourceId]
  )
  const dialogDestNode = useMemo(() =>
    nodes.find(n => n.id === dialogDestId),
    [nodes, dialogDestId]
  )
  const dialogSourceLocation = dialogSourceNode?.data.poiLocationConfig
  const dialogDestLocation = dialogDestNode?.data.poiLocationConfig
  const dialogHasValidLocations = !!(
    dialogSourceLocation?.latitude && dialogSourceLocation?.longitude &&
    dialogDestLocation?.latitude && dialogDestLocation?.longitude
  )

  // Update config helper
  const updateConfig = useCallback((updates: Partial<RouteNodeConfig>) => {
    updateNode(node.id, {
      routeNodeConfig: {
        ...config,
        ...updates,
      } as RouteNodeConfig,
    })
  }, [node.id, config, updateNode])

  // Format distance
  const formatDistance = (meters: number) => {
    if (meters < 1000) return `${Math.round(meters)}m`
    return `${(meters / 1000).toFixed(1)}km`
  }

  // Open auto route dialog
  const handleOpenAutoRouteDialog = () => {
    // Reset dialog state to current config values
    setDialogSourceId(config?.sourceNodeId || '')
    setDialogDestId(config?.destinationNodeId || '')
    setDialogTravelMode(config?.travelMode || 'foot-walking')
    setShowAutoRouteDialog(true)
  }

  // Handle generate route from dialog
  const handleGenerateRoute = async () => {
    if (!dialogHasValidLocations || !dialogSourceLocation || !dialogDestLocation) {
      setError('Both locations must have valid coordinates')
      return
    }

    setIsGenerating(true)
    setError(null)

    try {
      const result = await workflowDesignerApi.generateRoute(adventureId, {
        sourceLatitude: dialogSourceLocation.latitude,
        sourceLongitude: dialogSourceLocation.longitude,
        targetLatitude: dialogDestLocation.latitude,
        targetLongitude: dialogDestLocation.longitude,
        travelMode: dialogTravelMode,
      })

      // API returns [lat, lng] format - use directly
      const routePoints: [number, number][] = result.coordinates as [number, number][]

      updateConfig({
        sourceNodeId: dialogSourceId,
        destinationNodeId: dialogDestId,
        travelMode: dialogTravelMode,
        routePoints,
        distanceMeters: result.distanceMeters,
        durationSeconds: result.durationSeconds,
        dataSource: 'auto' as RouteDataSource,
        lastGeneratedAt: new Date().toISOString(),
      })

      setShowAutoRouteDialog(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate route')
    } finally {
      setIsGenerating(false)
    }
  }

  // Handle GPX import
  const handleGpxImport = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      const result = parseGpxFile(content)

      if (isGpxParseError(result)) {
        setError(`GPX Import Error: ${result.message}`)
        return
      }

      updateConfig({
        routePoints: result.coordinates,
        distanceMeters: result.distanceMeters,
        dataSource: 'gpx' as RouteDataSource,
        gpxFileName: file.name,
        lastGeneratedAt: new Date().toISOString(),
      })
      setError(null)
    }
    reader.onerror = () => {
      setError('Failed to read GPX file')
    }
    reader.readAsText(file)

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [updateConfig])

  // Handle point editor save
  const handlePointEditorSave = useCallback((points: [number, number][]) => {
    // Calculate distance
    let distance = 0
    for (let i = 1; i < points.length; i++) {
      const lat1 = points[i - 1][0] * Math.PI / 180
      const lat2 = points[i][0] * Math.PI / 180
      const dLat = (points[i][0] - points[i - 1][0]) * Math.PI / 180
      const dLon = (points[i][1] - points[i - 1][1]) * Math.PI / 180
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
      distance += 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    }

    updateConfig({
      routePoints: points,
      distanceMeters: distance,
      dataSource: 'manual' as RouteDataSource,
      lastGeneratedAt: new Date().toISOString(),
    })
  }, [updateConfig])

  // Handle clear route
  const handleClearRoute = () => {
    updateConfig({
      routePoints: [],
      distanceMeters: undefined,
      durationSeconds: undefined,
      dataSource: 'auto' as RouteDataSource,
      gpxFileName: undefined,
      lastGeneratedAt: undefined,
    })
  }

  const hasRoutePoints = config?.routePoints && config.routePoints.length > 0

  return (
    <Box>
      {/* Display Options - Always visible at top */}
      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
        Display Options
      </Typography>

      {/* Travel Mode */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Travel Mode</InputLabel>
        <Select
          value={config?.travelMode || 'foot-walking'}
          label="Travel Mode"
          onChange={(e) => updateConfig({ travelMode: e.target.value as RouteTravelMode })}
        >
          {ROUTE_TRAVEL_MODES.map((mode) => (
            <MenuItem key={mode.value} value={mode.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {travelModeIcons[mode.value]}
                {mode.label}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Line color */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          Line Color
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {ROUTE_COLORS.map((color) => (
            <Box
              key={color.value}
              onClick={() => updateConfig({ lineColor: color.value })}
              sx={{
                width: 28,
                height: 28,
                backgroundColor: color.value,
                borderRadius: 0.5,
                cursor: 'pointer',
                border: config?.lineColor === color.value ? '2px solid #000' : '1px solid #ccc',
                '&:hover': { transform: 'scale(1.1)' },
                transition: 'transform 0.1s',
              }}
              title={color.label}
            />
          ))}
        </Box>
      </Box>

      {/* Line style */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Line Style</InputLabel>
        <Select
          value={config?.lineStyle || 'dashed'}
          label="Line Style"
          onChange={(e) => updateConfig({ lineStyle: e.target.value as RouteLineStyle })}
        >
          {ROUTE_LINE_STYLES.map((style) => (
            <MenuItem key={style.value} value={style.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 3,
                    backgroundColor: config?.lineColor || '#9C27B0',
                    borderRadius: 1,
                    ...(style.value === 'dashed' && {
                      backgroundImage: `repeating-linear-gradient(90deg, ${config?.lineColor || '#9C27B0'} 0 6px, transparent 6px 12px)`,
                      backgroundColor: 'transparent',
                    }),
                    ...(style.value === 'dotted' && {
                      backgroundImage: `repeating-linear-gradient(90deg, ${config?.lineColor || '#9C27B0'} 0 3px, transparent 3px 6px)`,
                      backgroundColor: 'transparent',
                    }),
                  }}
                />
                {style.label}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Line opacity */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          Line Opacity: {Math.round((config?.lineOpacity ?? 0.5) * 100)}%
        </Typography>
        <Slider
          size="small"
          value={config?.lineOpacity ?? 0.5}
          onChange={(_, value) => updateConfig({ lineOpacity: value as number })}
          min={0.1}
          max={1}
          step={0.1}
          marks={[
            { value: 0.1, label: '10%' },
            { value: 0.5, label: '50%' },
            { value: 1, label: '100%' },
          ]}
        />
      </Box>

      {/* Arrow style */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Direction Arrows</InputLabel>
        <Select
          value={config?.arrowStyle || 'none'}
          label="Direction Arrows"
          onChange={(e) => updateConfig({ arrowStyle: e.target.value as RouteArrowStyle })}
        >
          {ROUTE_ARROW_STYLES.map((style) => (
            <MenuItem key={style.value} value={style.value}>
              {style.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ my: 2 }} />

      {/* Route Data Section */}
      <Typography variant="subtitle2" gutterBottom>
        Route Data
      </Typography>

      {hasRoutePoints ? (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Chip
              label={`${config?.routePoints?.length} points`}
              size="small"
              variant="outlined"
            />
            {config?.distanceMeters && (
              <Chip
                label={formatDistance(config.distanceMeters)}
                size="small"
                color="primary"
              />
            )}
            <Chip
              label={config?.dataSource === 'gpx' ? 'GPX' : config?.dataSource === 'manual' ? 'Manual' : 'Auto'}
              size="small"
              color={config?.dataSource === 'gpx' ? 'info' : config?.dataSource === 'manual' ? 'warning' : 'success'}
              variant="outlined"
            />
          </Box>
          {config?.gpxFileName && (
            <Typography variant="caption" color="text.secondary" display="block">
              File: {config.gpxFileName}
            </Typography>
          )}
          {sourceNode && destinationNode && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              L{sourceNode.data.sequenceNumber || '?'} → L{destinationNode.data.sequenceNumber || '?'}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No route data. Generate, import, or draw a route.
        </Typography>
      )}

      {/* Route action buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RouteIcon />}
          onClick={handleOpenAutoRouteDialog}
        >
          Auto Route
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFile />}
          onClick={() => fileInputRef.current?.click()}
        >
          Import GPX
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Edit />}
          onClick={() => setShowPointEditor(true)}
        >
          Edit Points
        </Button>
        {hasRoutePoints && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<Clear />}
            onClick={handleClearRoute}
          >
            Clear
          </Button>
        )}
      </Box>

      <input
        type="file"
        ref={fileInputRef}
        accept=".gpx"
        style={{ display: 'none' }}
        onChange={handleGpxImport}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Auto Route Dialog */}
      <Dialog
        open={showAutoRouteDialog}
        onClose={() => setShowAutoRouteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Auto Route</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select start and end locations to generate a route automatically.
          </Typography>

          <Autocomplete
            size="small"
            options={locationOptions}
            value={locationOptions.find(o => o.id === dialogSourceId) || null}
            onChange={(_, newValue) => setDialogSourceId(newValue?.id || '')}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField {...params} label="Source Location" placeholder="Select start location" />
            )}
            sx={{ mb: 2 }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          <Autocomplete
            size="small"
            options={locationOptions}
            value={locationOptions.find(o => o.id === dialogDestId) || null}
            onChange={(_, newValue) => setDialogDestId(newValue?.id || '')}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField {...params} label="Destination Location" placeholder="Select end location" />
            )}
            sx={{ mb: 2 }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {/* Travel mode */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Travel Mode</InputLabel>
            <Select
              value={dialogTravelMode}
              label="Travel Mode"
              onChange={(e) => setDialogTravelMode(e.target.value as RouteTravelMode)}
            >
              {ROUTE_TRAVEL_MODES.map((mode) => (
                <MenuItem key={mode.value} value={mode.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {travelModeIcons[mode.value]}
                    {mode.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!dialogHasValidLocations && dialogSourceId && dialogDestId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Both locations must have coordinates configured.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAutoRouteDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateRoute}
            disabled={!dialogHasValidLocations || isGenerating}
            startIcon={isGenerating ? <CircularProgress size={16} /> : <Refresh />}
          >
            {isGenerating ? 'Generating...' : 'Generate Route'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Route Point Editor Dialog */}
      <RoutePointEditor
        open={showPointEditor}
        onClose={() => setShowPointEditor(false)}
        routePoints={config?.routePoints || []}
        onSave={handlePointEditorSave}
        sourceLocation={sourceLocation ? {
          lat: sourceLocation.latitude,
          lng: sourceLocation.longitude,
          label: sourceNode?.data.label,
        } : undefined}
        destinationLocation={destLocation ? {
          lat: destLocation.latitude,
          lng: destLocation.longitude,
          label: destinationNode?.data.label,
        } : undefined}
        lineColor={config?.lineColor}
      />

      <Divider sx={{ my: 2 }} />

      {/* Export Settings Section */}
      <Typography variant="subtitle2" gutterBottom>
        GPX Export Settings
      </Typography>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={config?.exportable ?? true}
            onChange={(e) => updateConfig({ exportable: e.target.checked })}
          />
        }
        label="Allow GPX Export"
        sx={{ mb: 1, display: 'block' }}
      />

      {(config?.exportable ?? true) && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Export Availability</InputLabel>
            <Select
              value={config?.exportDisclosure || 'always'}
              label="Export Availability"
              onChange={(e) => updateConfig({ exportDisclosure: e.target.value as RouteExportDisclosure })}
            >
              {ROUTE_EXPORT_DISCLOSURE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label="Export Label (optional)"
            placeholder="e.g., Day 1 Hike"
            value={config?.exportLabel || ''}
            onChange={(e) => updateConfig({ exportLabel: e.target.value })}
            helperText="Custom name shown in exported GPX file"
          />
        </>
      )}
    </Box>
  )
}
